import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";

import { EXRLoader } from "three/examples/jsm/loaders/EXRLoader.js";
import fragment from "./shader/fragment.glsl";
import vertex from "./shader/vertex.glsl";
import GUI from "lil-gui";
import gsap from "gsap";
import disp from "../displacement.exr";
import normals from "../normal.png";
import map from "../stickers.png";

export default class Sketch {
  constructor(options) {
    this.scene = new THREE.Scene();

    this.container = options.dom;
    this.width = this.container.offsetWidth;
    this.height = this.container.offsetHeight;
    this.renderer = new THREE.WebGLRenderer();
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.width, this.height);
    this.renderer.setClearColor(0xeeeeee, 1);
    // this.renderer.physicallyCorrectLights = true;
    this.renderer.outputEncoding = THREE.sRGBEncoding;

    this.container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(
      70,
      window.innerWidth / window.innerHeight,
      0.001,
      1000
    );

    // var frustumSize = 10;
    // var aspect = window.innerWidth / window.innerHeight;
    // this.camera = new THREE.OrthographicCamera( frustumSize * aspect / - 2, frustumSize * aspect / 2, frustumSize / 2, frustumSize / - 2, -1000, 1000 );
    this.camera.position.set(0, 0, 2);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.time = 0;

    this.dracoLoader = new DRACOLoader();
    this.dracoLoader.setDecoderPath(
      "https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/js/libs/draco/"
    ); // use a full url path
    this.gltf = new GLTFLoader();
    this.gltf.setDRACOLoader(this.dracoLoader);

    this.isPlaying = true;

    let r = new THREE.AmbientLight(16777215,.75);
            this.scene.add(r);
            let s = new THREE.DirectionalLight(16777215,.75);
            s.position.set(2, 4, 1);
            this.scene.add(s);

    new EXRLoader().load(disp, (texture, textureData) => {
      this.displacementTexture = texture;
      this.addObjects();
      this.resize();
      this.render();
      this.setupResize();
    });

    this.settings();
  }

  settings() {
    let that = this;
    this.settings = {
      progress: 0,
      progress1: 0,
      rot: 0,
    };
    // this.gui = new GUI();
    // this.gui.add(this.settings, "progress", -1, 1, 0.01);
    // this.gui.add(this.settings, "progress1", -1, 1, 0.01);
    // this.gui.add(this.settings, "rot", -3, 3, 0.01);
  }

  setupResize() {
    window.addEventListener("resize", this.resize.bind(this));
  }

  resize() {
    this.width = this.container.offsetWidth;
    this.height = this.container.offsetHeight;
    this.renderer.setSize(this.width, this.height);
    this.camera.aspect = this.width / this.height;

    this.camera.updateProjectionMatrix();
  }

  addObjects() {
    let that = this;
    let Da = 1.8
    this.material = new THREE.MeshPhongMaterial({
      transparent: !0,
      alphaTest: 0.5,
      side: THREE.DoubleSide,
      // color: 0xff0000,
      map: new THREE.TextureLoader().load(map),
      displacementMap: this.displacementTexture,
      normalMap: new THREE.TextureLoader().load(normals),
    });

    this.aspect = 1;
    this.scale = 0.5;
    this.rotation = 0;

    this.material.onBeforeCompile = (shader) => {
      shader.uniforms.translate = {
        value: new THREE.Vector2(0, 0),
      };
      shader.uniforms.scale = {
        value: new THREE.Vector2(this.scale, this.scale * 6),
      };
      shader.uniforms.aspect = {
        value: new THREE.Vector2(this.aspect * 2, 2),
      };
      shader.uniforms.rotation = {
        value: THREE.MathUtils.degToRad(this.rotation),
      };
      shader.uniforms.distance = {
        value: this.distance,
      };
      shader.vertexShader = shader.vertexShader.replace(
        "#include <clipping_planes_pars_vertex>",
        `
#include <clipping_planes_pars_vertex>
uniform float rotation;
uniform float distance;
uniform vec2 aspect;
uniform vec2 translate;
uniform vec2 scale;

varying vec2 vDisplacementUv;

#define BASE_DISTANCE 1.4
#define DISPLACEMENT_DISTANCE 1.25

float map(float value, float min1, float max1, float min2, float max2) {
  return min2 + (value - min1) * (max2 - min2) / (max1 - min1);
}

vec2 rotate(vec2 v, float a) {
  float s = sin(a);
  float c = cos(a);
  mat2 m = mat2(c, -s, s, c);
  return m * v;
}
`
      );
      shader.vertexShader = shader.vertexShader.replace(
        "#include <project_vertex>",
        `
        // #include <project_vertex>
// vec2 offset = (position.xy / 2.);
// vec2 rotated = offset;
vec2 pos = position.xy/2. *scale+ translate;

// pos = position.xy;
float u = fract(pos.x + 0.5);
float v = fract(map(pos.y, -${Da}, ${Da}, 0.0, 1.0));
// v = pos.y/4.;
vec2 displacementUv = vec2(u, v);
float displacement = (texture2D(displacementMap, displacementUv).r - 0.5) * 2.0;

float baseDist = BASE_DISTANCE + DISPLACEMENT_DISTANCE * displacement;
vec2 positionOnCircle = rotate(vec2(0.0, baseDist), pos.x * PI2);
vec3 displacedPosition = vec3(positionOnCircle.x, pos.y, positionOnCircle.y);
vec4 mvPosition = vec4(displacedPosition, 1.0);

vDisplacementUv = displacementUv;
vDisplacementUv = vec2(u,v);
mvPosition = modelViewMatrix * mvPosition;
 gl_Position = projectionMatrix * viewMatrix * vec4(displacedPosition, 1.0);
`
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <common>",
        `
#include <common>
varying vec2 vDisplacementUv;
`
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <normal_fragment_maps>",
        `
#include <normal_fragment_maps>
normal = texture2D(normalMap, vDisplacementUv).xyz * 2.0 - 1.0;
// diffuseColor.rgb= vec3(vDisplacementUv,0.);
`
      );
      this.material.userData.shader = shader;
    };

    let a = new THREE.PlaneBufferGeometry(2, 2, 100, 100);
    this.mesh = new THREE.Mesh(a, this.material);
    // this.mesh = new THREE.Mesh(a, new THREE.MeshBasicMaterial({
    //   map: new THREE.TextureLoader().load(normals)
    // }));
    this.scene.add(this.mesh);
  }

  stop() {
    this.isPlaying = false;
  }

  play() {
    if (!this.isPlaying) {
      this.isPlaying = true;
      this.render();
    }
  }

  render() {
    if (!this.isPlaying) return;
    this.time += 0.05;
    // this.material.uniforms.time.value = this.time;
    requestAnimationFrame(this.render.bind(this));
    this.renderer.render(this.scene, this.camera);

    this.camera.position.y = 0.3*Math.sin(this.time*0.3)
    this.camera.position.x = 0.3*Math.cos(this.time*0.3)
    this.camera.lookAt(0,0,0)

    this.settings.progress = 0.15*Math.sin(this.time*0.1)
    if(this.material.userData.shader) {
      this.material.userData.shader.uniforms.translate.value.x = this.settings.progress
      this.material.userData.shader.uniforms.translate.value.y = this.settings.progress
      this.material.userData.shader.uniforms.rotation.value = this.settings.rot
    }
  }
}

new Sketch({
  dom: document.getElementById("container"),
});
